import { colorForPerson, firstName } from "../lib/personColor";
import { SIGN_IN_SETTINGS_ROW_TITLE } from "../lib/authCopy";
import { SignOutButton } from "./SignOutButton";
import type { ResolvedView } from "../lib/routes";

export interface SidebarProps {
  active: ResolvedView;
  /** Sprint-003 A6 gate — same flag `TabBar`'s Team tab and `MoreView`'s
   * Projects row already use; gates the Team/Projects rows here too. */
  canHandOutWork: boolean;
  /** Sprint-004 B4 gate — same flag `TabBar`'s Admin tab uses; gates the
   * divider + People/Chores rows here too. */
  isAdmin: boolean;
  /** The signed-in person's id (for `colorForPerson`) and full name (for
   * `firstName`/the footer's avatar initial) — same two fields `app.tsx`
   * already threads to every other authenticated screen. */
  id: string;
  name: string;
  onNavigate: (href: string) => void;
  onLogout: () => void;
}

interface NavItem {
  view: ResolvedView;
  href: string;
  label: string;
}

/**
 * Sprint-008 A2/A4 (design-desktop-phase-a.md §1) — the persistent desktop
 * left sidebar, `≥1024px` only (mounted by `app.tsx` behind
 * `hooks/useIsDesktop.ts`; never mounted alongside `TabBar` — see that
 * hook's own docstring). Nav inventory/order/role-gating mirrors §1.2's
 * table exactly, mirroring the SAME gates `TabBar.tsx`/`useIsAdmin.ts`
 * already enforce (no new authz decision made here).
 *
 * A2 shipped this component with the "Dashboard" row deliberately omitted
 * (Dashboard/`/dashboard` didn't exist yet — A4 was a separate, not-yet-
 * built epic), flagged as "a one-line change once A4 ships a real
 * `/dashboard` route + view." A4 (this sprint) resolves that flag: the
 * Dashboard row is now first in the list (§1.2's own order), and the
 * wordmark links to `/dashboard` (not `/today`) to match — both per the
 * one-line follow-up the A2 PR body itself named.
 *
 * Rows are real `<a href>` elements (not `<button onClick>`, `TabBar`'s
 * pattern) — a `<nav>` landmark's rows read as "links" to a screen
 * reader, support native keyboard activation, and preserve modifier-click
 * ("open in a new tab") behavior via `handleNavClick` below, none of
 * which a button-based row would give for free.
 */
export function Sidebar({
  active,
  canHandOutWork,
  isAdmin,
  id,
  name,
  onNavigate,
  onLogout,
}: SidebarProps) {
  function handleNavClick(event: MouseEvent, href: string) {
    // Left-click, no modifier held — intercept and hand off to the
    // client-side router. Anything else (middle-click, cmd/ctrl/shift-
    // click, a `preventDefault`ed synthetic event) is left alone so the
    // browser's own "open in new tab"/"open in new window" behavior keeps
    // working, matching how every serious SPA router's own `Link`
    // component behaves.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate(href);
  }

  function renderItem({ view, href, label }: NavItem) {
    const isActive = active === view;
    return (
      <a
        key={view}
        href={href}
        class="sidebar-nav-item"
        aria-current={isActive ? "page" : undefined}
        onClick={(event) => handleNavClick(event, href)}
      >
        {label}
      </a>
    );
  }

  return (
    <div class="sidebar-shell">
      {/* Sprint-008 A4 — links to `/dashboard`, matching §1.1's ASCII
       * diagram ("`.sidebar-wordmark` (link -> /dashboard)") now that a
       * real Dashboard route/view exists. */}
      <a
        href="/dashboard"
        class="sidebar-wordmark"
        onClick={(event) => handleNavClick(event, "/dashboard")}
      >
        Hayloft
      </a>

      <nav class="sidebar-nav" aria-label="Main">
        {renderItem({ view: "dashboard", href: "/dashboard", label: "Dashboard" })}
        {renderItem({ view: "today", href: "/today", label: "Today" })}
        {renderItem({ view: "mylist", href: "/my", label: "My list" })}
        {/* Sprint-009 B4 (design-phase-b.md §1.1) — everyone-visible,
         * placed beside Today/My list (a read-first personal-schedule
         * row), not in the manager-gated block below. */}
        {renderItem({ view: "calendar", href: "/calendar", label: "Calendar" })}
        {canHandOutWork ? renderItem({ view: "team", href: "/team", label: "Team" }) : null}
        {renderItem({ view: "animals", href: "/animals", label: "Animals" })}
        {/* Sprint-017 E7 (ADR-003 §3.3) — Projects is open to every
         * authenticated role now (scope server-decided), like Animals/
         * Calendar above; no longer `canHandOutWork`-gated. */}
        {renderItem({ view: "projects", href: "/projects", label: "Projects" })}
        {/* Sprint-010 C4 (design-phase-c.md §1.1 row 8) — same gate as
         * Team/Projects (`canHandOutWork` — admin OR manager, per
         * ADR-002 A6 decision 8.2), positioned directly after Projects. */}
        {canHandOutWork
          ? renderItem({ view: "contacts", href: "/contacts", label: "Contacts" })
          : null}
        {/* Sprint-009 B4 (design-phase-b.md §1.1 row 8) — same gate as
         * Team/Projects (`canHandOutWork` — admin OR manager, per
         * ADR-002 A5 decision 6's own authz line for all four reports). */}
        {canHandOutWork
          ? renderItem({ view: "reports", href: "/reports", label: "Reports" })
          : null}
        {isAdmin ? <div class="sidebar-divider" role="separator" /> : null}
        {isAdmin ? renderItem({ view: "people", href: "/admin/people", label: "People" }) : null}
        {isAdmin ? renderItem({ view: "chores", href: "/admin/chores", label: "Chores" }) : null}
        {/* Sprint-009 B3 (design-phase-b.md §1.1 row 11) — `admin`/`api`
         * only (NOT manager, unlike Reports above) — Activity reads the
         * audit ledger, a stricter class than an operational report
         * (ADR-002 A5 decision 5.1). */}
        {isAdmin ? renderItem({ view: "activity", href: "/activity", label: "Activity" }) : null}
        {/* Sprint-014 C1 (config-page-ia-layout-2026-07-10.md §1) — same
         * admin/api-only gate as People/Chores/Activity above (NOT
         * manager), positioned directly after Activity, before the
         * "Your sign-in" row. */}
        {isAdmin ? renderItem({ view: "config", href: "/config", label: "Config" }) : null}
        {/* R-A0-1 (UX validation, non-negotiable) — "Your sign-in," never
         * "Settings." Matches `lib/authCopy.ts::SIGN_IN_SETTINGS_ROW_TITLE`
         * verbatim, not a second hand-typed copy of the same string. */}
        {renderItem({ view: "settings", href: "/settings", label: SIGN_IN_SETTINGS_ROW_TITLE })}
      </nav>

      <div class="sidebar-footer">
        <span class="avatar" aria-hidden="true" style={{ background: colorForPerson(id) }}>
          {firstName(name).charAt(0) || name.charAt(0)}
        </span>
        <span class="sidebar-footer-name">{firstName(name) || name}</span>
        {/* UX review 2026-10 — same confirm-first sign-out as phone's
         * More / Your sign-in (was an instant "Done for now"). */}
        <SignOutButton onSignOut={onLogout} variant="link" />
      </div>
    </div>
  );
}
